import React from "react";
import Text from "@components/shared/text";
import Breadcrumb from "@components/shared/breadcrumb";
import CustomSelect from "@components/shared/customSelect";
import BlogPosts from "@components/shared/blogPosts";
import {
    useInfiniteQuery,
    UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { fetchDataFromFront } from "@gql/graphQLRequestServer";
import MetaTags from "@components/shared/metaTags";
import { GQLDomains } from "@gql/types/gql";
import { translate } from "@helpers/translationObjectFormatter";
import { ICustomDomain, TranslationsObject } from "@genericTypes/sharedTypes";
import {
    GQLCategories,
    GQLPosts,
    GQLCategoryBySlugPaginationQuery,
    GQLCategoryBySlugPaginationQueryVariables,
} from "@gql/types/gql";
import { GetStaticPropsContext } from "next";
import { useQuery } from "@tanstack/react-query";
import { CategoryBySlugPaginationDocument } from "@gql/types/gql";
import initialCall, { initialCallPaths } from "@helpers/initial-call";
import { useRouter } from "next/router";
import {
    getDescriptionForMetaTags,
    getKeyWordsForMetaTags,
} from "@lib/sharedUtils";
import { BreadcrumbItem } from "@genericTypes/sharedTypes";

const BLOGS_PER_PAGE: number = 9;

const Page = () => {
    const router = useRouter();

    const { data: domainInfo } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const useInfiniteCategoryBySlugQuery = <
        TData = GQLCategoryBySlugPaginationQuery,
        TError = unknown,
    >(
        pageParamKey: keyof GQLCategoryBySlugPaginationQueryVariables,
        variables: GQLCategoryBySlugPaginationQueryVariables,
        options?: UseInfiniteQueryOptions<
            GQLCategoryBySlugPaginationQuery,
            TError,
            TData
        >,
    ) => {
        return useInfiniteQuery<
            GQLCategoryBySlugPaginationQuery,
            TError,
            TData
        >(
            ["postsByCategorySlug", variables],
            (metaData) =>
                fetchDataFromFront<
                    GQLCategoryBySlugPaginationQuery,
                    GQLCategoryBySlugPaginationQueryVariables
                >(CategoryBySlugPaginationDocument as string, {
                    ...variables,
                    ...(metaData.pageParam
                        ? { [pageParamKey]: metaData.pageParam as number }
                        : {}),
                })(),
            options,
        );
    };

    const { data, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
        useInfiniteCategoryBySlugQuery(
            "offset",
            {
                limit: BLOGS_PER_PAGE,
                offset: 0,
                domainId: domainInfo?.domain?.id,
                slug: router.query.categorySlug as string,
                language: process.env.NEXT_PUBLIC_DEFAULT_LANG,
            },
            {
                getNextPageParam(lastPage, allPages) {
                    if (lastPage && lastPage.category?.posts)
                        return lastPage.category.posts.length < BLOGS_PER_PAGE
                            ? undefined
                            : allPages.length * BLOGS_PER_PAGE;
                },
            },
        );

    const { data: categories } = useQuery<GQLCategories[], Error>({
        queryKey: ["allCategoriesCategorySlug"],
    });
    const selectedCategory = categories?.find(
        (el) => el.slug === router.query.categorySlug,
    );
    const selectedCategoryName = selectedCategory?.label
        ? selectedCategory?.label
        : selectedCategory?.name;
    const arr = [
        {
            label: translate(translations, "several_general_breadcrumb_home"),
            link: "/",
            isActive: true,
            showOnMobile: true,
        },
        {
            label: translate(translations, "several_general_blogWord"),
            link: "/blog",
            isActive: true,
            showOnMobile: true,
        },
        {
            label: selectedCategoryName,
            link: "",
            isActive: false,
            showOnMobile: true,
        },
    ];
    const filteredCategories = categories?.filter(
        (el) => el.slug !== router.query.categorySlug && el.posts?.length,
    );

    return (
        <>
            <MetaTags
                title={`${selectedCategoryName as string} ${translate(
                    translations,
                    "several_siteMapPage_Blogs",
                )} | Several.com`}
                description={
                    getDescriptionForMetaTags(
                        domainInfo?.domain as GQLDomains,
                        translate(
                            translations,
                            "several_general_meta_description",
                        ),
                    ) as string
                }
                keyWords={
                    getKeyWordsForMetaTags(
                        domainInfo?.domain as GQLDomains,
                    ) as string
                }
                image="public/images25.png"
            />
            <section className="max-w-7xl mx-auto px-5">
                <Breadcrumb arr={arr as BreadcrumbItem[]} />
                <Text
                    type="mainTitle"
                    className="text-center text-4xl lg:leading-[63px] mt-4 px-5 leading-[47px] mb-5"
                >
                    {`${translate(
                        translations,
                        "several_general_discoverTopBlogs",
                    )} ${new Date().getFullYear()}`}
                </Text>
                <Text className="text-center  lg:text-xl text-lg lg:leading-9 leading-7 px-2 mt-2 text-[#58616D]">
                    {translate(
                        translations,
                        "several_blogPage_fromBeautyMindfulness",
                    )}
                </Text>

                <div className="max-w-lg  mx-auto mt-6 flex lg:flex-row flex-col">
                    <div className="relative w-full mx-auto mb-20">
                        <CustomSelect
                            className={`w-full `}
                            placeHolder={`${
                                selectedCategoryName
                                    ? selectedCategoryName
                                    : translate(
                                          translations,
                                          "several_general_chooseTopicsWord",
                                      )
                            }`}
                            categorySlugPage={true}
                            autoComplete={false}
                            onChange={(category: GQLCategories) => {
                                void router.push(
                                    `/blog/category/${category.slug as string}`,
                                );
                            }}
                            options={(
                                filteredCategories as GQLCategories[]
                            )?.map((el) => ({
                                id: el.id as number,
                                label: el.label ?? el.name ?? "",
                                slug: el.slug ?? "",
                            }))}
                        />
                    </div>
                </div>
                {error ? (
                    <div>
                        {translate(
                            translations,
                            "several_articlesPage_failedMessage",
                        )}
                    </div>
                ) : (
                    <BlogPosts
                        posts={
                            data?.pages
                                .map((i) => i.category?.posts)
                                .flat() as GQLPosts[]
                        }
                        title=""
                        onClick={() => {
                            void fetchNextPage();
                        }}
                        type="buttonWithPagination"
                        isFetchingNextPage={isFetchingNextPage}
                        hasNextPage={hasNextPage}
                    />
                )}
            </section>
        </>
    );
};
export function getStaticPaths() {
    return initialCallPaths();
}
export const getStaticProps = async (ctx: GetStaticPropsContext) => {
    return await initialCall("CategorySlug", ctx);
};
export default Page;
